import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete,
  DialogContentText,
  IconButton,
  Tooltip,
} from '@mui/material'
import { DeleteOutlined } from '@mui/icons-material'
import { Integration, IntegrationConfig, ServiceAccount } from '@sendhub/integrations/api-client-typescript-axios'
import { useFormikContext } from 'formik'
import lodash from 'lodash'
import { useState, FormEventHandler } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { selectAllIntegrationConfigs, updateIntegrationConfig } from '../store/integration-config/integration-configs.slice'
import { selectIntegrationGroupById } from '../store/integration-group/integration-groups.slice'
import { selectIntegrationById, selectIntegrationsEntities } from '../store/integration/integrations.slice'
import { useAppDispatch } from '../store/root'
import {
  createServiceAccount,
  deleteServiceAccount,
  selectAllServiceAccounts,
  selectServiceAccountsEntities,
} from '../store/service-account/service-accounts.slice'
import { useIntegrationsAccess } from '../store/integrations-access'

export interface SelectAccountParams {
  integrationConfig: IntegrationConfig
  hideConnectAccountButton?: boolean
  /** Hide the per-account delete button, e.g. for automatically managed accounts. */
  hideDeleteAccountButton?: boolean
}
export default function SelectAccount({ integrationConfig, hideConnectAccountButton, hideDeleteAccountButton }: SelectAccountParams) {
  //   const handleSelectionChange = (event) => {
  //     setSelectedAccount(event.target.value)
  //   }
  const integration = useSelector(selectIntegrationById(integrationConfig.integrationId))
  const integrationGroup = useSelector(selectIntegrationGroupById(integration?.integrationGroupId || ''))
  const serviceAccounts = useSelector(selectAllServiceAccounts).filter(
    (sA) => sA.integrationGroupId === integration?.integrationGroupId || ''
  )
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const modalInitialValue = { nickname: '', apiKey: '' }
  const [modalValue, setModalValue] = useState(modalInitialValue)
  const [accountToDelete, setAccountToDelete] = useState<ServiceAccount | null>(null)
  // Captured when the dialog opens: the account leaves the list once deleted,
  // and the title must not go blank while the dialog fades out.
  const [accountToDeleteLabel, setAccountToDeleteLabel] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const allIntegrationConfigs = useSelector(selectAllIntegrationConfigs)
  const integrationsById = useSelector(selectIntegrationsEntities)
  const dispatch = useAppDispatch()
  const formik = useFormikContext<IntegrationConfig>()
  const { canEdit, canDelete } = useIntegrationsAccess()

  const handleModalClose = () => {
    setModalValue(modalInitialValue)
    setModalIsOpen(false)
  }

  const configsUsingAccountToDelete = accountToDelete ? allIntegrationConfigs.filter((c) => c.serviceAccountId === accountToDelete.id) : []

  const handleDeleteDialogClose = () => {
    if (!isDeleting) setAccountToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return
    setIsDeleting(true)
    const label = accountToDeleteLabel
    const result = await dispatch(deleteServiceAccount(accountToDelete.id))
    setIsDeleting(false)
    if (deleteServiceAccount.fulfilled.match(result)) {
      // Keep this form in step with what the server just did.
      if (formik.values.serviceAccountId === accountToDelete.id) formik.setFieldValue('serviceAccountId', '')
      if (integrationConfig.id && result.payload.disabledIntegrationConfigIds.includes(integrationConfig.id))
        formik.setFieldValue('enabled', false)
      toast.success(`Deleted ${label}`)
      result.payload.warnings.forEach((warning) => toast.warn(warning, { autoClose: false }))
      setAccountToDelete(null)
    } else {
      toast.error(`Couldn't delete ${label}. Please try again.`)
    }
  }

  if (!integrationGroup) return null

  const formLabel = `select-${integration?.integrationGroupId}-account-radio-group-label`
  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!integration?.integrationGroupId)
      throw new Error(`integration.integrationGroupId is invalid! ("${integration?.integrationGroupId}")`)
    const result = await dispatch(
      createServiceAccount({
        nickname: modalValue.nickname,
        credentials: { apiKey: modalValue.apiKey },
        integrationGroupId: integration.integrationGroupId,
      })
    )
    if (createServiceAccount.fulfilled.match(result)) {
      formik.setFieldValue('serviceAccountId', result.payload.id)
    }
    handleModalClose()
  }
  const getOptionLabel = (id: string) => {
    const sA = serviceAccounts.find((sA) => sA.id === id)
    if (!sA) return ''
    if (!('apiKey' in sA.credentials)) return sA.nickname

    const apiKeyPreview =
      'apiKey' in sA.credentials && sA.credentials.apiKey && sA.credentials.apiKey.trim().length > 0
        ? ` (********${sA.credentials.apiKey.substring(sA.credentials.apiKey.length - 4)})`
        : ''

    return `${sA.nickname}${apiKeyPreview}`
  }
  return (
    <>
      {
        /** !serviceAccounts.length ? null :*/ <Grid item xs={6}>
          <Autocomplete
            disableClearable
            openOnFocus
            disabled={!canEdit}
            id="serviceAccountId"
            value={formik.values.serviceAccountId}
            onChange={(event, value) => formik.setFieldValue('serviceAccountId', value)}
            options={serviceAccounts.map((sA) => sA.id)}
            getOptionLabel={getOptionLabel}
            filterOptions={(options) => {
              return [...options, 'button']
            }}
            renderOption={(props, option, state) => {
              if (option === 'button') {
                if (hideConnectAccountButton || !canEdit) return null
                return (
                  <Button
                    key="button"
                    sx={{ width: '100%', p: 0, m: 0, paddingY: 1, marginTop: 1 }}
                    variant="text"
                    color="primary"
                    onClick={() => {
                      setModalIsOpen(true)
                    }}
                  >
                    Connect {integrationGroup.name} Account
                  </Button>
                )
              }
              const optionLabel = getOptionLabel(option)
              if (hideDeleteAccountButton || !canDelete) return <li {...props}>{optionLabel}</li>
              return (
                <li {...props} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{optionLabel}</span>
                  <Tooltip title="Delete account">
                    <IconButton
                      size="small"
                      aria-label={`Delete ${optionLabel}`}
                      onClick={(event) => {
                        // Don't also select the option being deleted.
                        event.stopPropagation()
                        setAccountToDeleteLabel(optionLabel)
                        setAccountToDelete(serviceAccounts.find((sA) => sA.id === option) ?? null)
                      }}
                    >
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name="serviceAccountId"
                label={`Select ${integrationGroup.name} Account`}
                error={formik.touched.serviceAccountId && Boolean(formik.errors.serviceAccountId)}
                helperText={formik.touched.serviceAccountId && formik.errors.serviceAccountId}
              />
            )}
          />
        </Grid>
      }
      {/* <Grid item xs={6}>
        <Button
        
          sx={{ width: '100%' }}
          variant="outlined"
          color="primary"
          onClick={() => {
            setModalIsOpen(true)
          }}
        >
          Connect {integrationGroup.name} Account
        </Button>
      </Grid> */}
      {/* </Box> */}
      {/* </FormControl> */}
      <Dialog open={modalIsOpen} onClose={handleModalClose} aria-labelledby="form-dialog-nickname">
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-nickname">Add a {integrationGroup.name} Account</DialogTitle>
          <DialogContent>
            {/* <DialogContentText></DialogContentText> */}
            <Grid container direction="column">
              <Grid item>
                <TextField
                  // error={modalValue.nickname == ''}
                  fullWidth
                  autoFocus
                  margin="dense"
                  id="nickname"
                  value={modalValue.nickname}
                  onChange={(event) => setModalValue({ ...modalValue, nickname: event.target.value })}
                  label="Nickname"
                  type="text"
                />
              </Grid>
              <Grid item>
                <TextField
                  // error={modalValue.api_key == ''}
                  fullWidth
                  margin="dense"
                  id="api_key"
                  value={modalValue.apiKey}
                  onChange={(event) => setModalValue({ ...modalValue, apiKey: event.target.value })}
                  label="API Key"
                  type="text"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={!modalValue.apiKey.trim() || !modalValue.nickname.trim()}>
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={accountToDelete !== null} onClose={handleDeleteDialogClose} aria-labelledby="delete-account-dialog-title">
        <DialogTitle id="delete-account-dialog-title">Delete {accountToDeleteLabel}?</DialogTitle>
        <DialogContent>
          {configsUsingAccountToDelete.length > 0 ? (
            <>
              <DialogContentText>Used by:</DialogContentText>
              <ul>
                {configsUsingAccountToDelete.map((c) => (
                  <li key={c.id}>
                    <strong>{integrationsById[c.integrationId]?.name ?? c.integrationId}</strong> ({c.enabled ? 'on' : 'off'})
                  </li>
                ))}
              </ul>
              <DialogContentText>These will be turned off and SendHub's webhooks removed from {integrationGroup.name}.</DialogContentText>
            </>
          ) : (
            <DialogContentText>This account isn't used by any integration.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting || !canDelete}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
